/**
 * Responsibility: the collection as a table.
 *
 * The auditing view. The grid is for looking at what you own and lives in
 * `ShelfGrid`, shared with the liked and watched pages; this is the one
 * where cost, gain and the date of the last valuation need to line up in
 * columns.
 *
 * Shows the value of the position rather than of one copy, since that is
 * what the total at the top is made of, and shows an unrecorded cost as
 * absent rather than as zero - a gain measured against a cost nobody
 * entered would be the whole value, which is flattering and wrong.
 */
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import { DataTable } from '@kit/components/data';
import type { DataColumn } from '@kit/components/data';
import { ToolbarIconButton } from '@kit/components/buttons';
import { EMPTY } from '@kit/utils';
import type { CollectionItem } from '../../api/client';
import { gradeNumber } from './collectionFilters';
import { CardThumb } from '../grading/CardThumb';
import { gradeSkin } from './gradingColours';
import { money, toNumber } from '../grading/money';

/** Matches the search and discovery row thumbnails. */
const ROW_THUMB_WIDTH = 46;

type ViewProps = {
  items: CollectionItem[];
  busy: boolean;
  onRemove: (item: CollectionItem) => void;
  onOpen: (item: CollectionItem) => void;
  /** Delete only appears in edit mode: it is the one action with no undo. */
  editing: boolean;
};

/**
 * Column widths are measured, not guessed: headings never wrap, so a
 * heading that does not fit its column is clipped. `tests/table-headers.mjs`
 * renders this table in a browser and fails on a clipped one.
 */
export function CollectionTable({
  items,
  busy,
  onRemove,
  onOpen,
  editing,
  maxHeight,
}: ViewProps & { maxHeight?: number }) {
  const columns: DataColumn<CollectionItem>[] = [
    {
      id: 'name',
      label: 'Card',
      render: (item) => (
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, py: 0.5 }}>
          {/* The same thumbnail the search and discovery rows carry, so
              the three tables read as one system. */}
          <CardThumb src={item.image_url} alt={item.name} width={ROW_THUMB_WIDTH} />
          <Stack spacing={0.25} sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {item.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {item.category === 'SPORTS' ? 'Sports' : 'Trading card'}
            </Typography>
          </Stack>
        </Stack>
      ),
      sortValue: (item) => item.name,
    },
    {
      id: 'grade',
      label: 'Grade',
      width: 120,
      render: (item) => (
        <Chip
          size="small"
          variant="outlined"
          label={
            item.is_graded ? `${item.grading_company} ${item.grade_label}` : item.grade_label
          }
          sx={
            item.is_graded
              ? {
                  borderColor: gradeSkin(item.grade_label).edge,
                  borderWidth: 2,
                  fontWeight: 700,
                }
              : undefined
          }
        />
      ),
      // **The number, not the label.** Sorting the text put "PSA 10"
      // above "PSA 9" by luck of the alphabet and "Grade 9.5" nowhere near
      // either. Raw copies have no grade and sort below every slab rather
      // than counting as a nought.
      sortValue: (item) =>
        (item.is_graded ? gradeNumber(item.grade_label) : null) ?? -1,
    },
    {
      id: 'set',
      label: 'Set',
      width: 150,
      render: (item) => (
        <Typography variant="body2" color={item.set_label ? 'text.primary' : 'text.secondary'} noWrap>
          {item.set_label ?? 'unknown'}
        </Typography>
      ),
      // Cards whose set could not be derived group at the end rather than
      // under a blank heading, which is where an empty string would put them.
      sortValue: (item) => item.set_label ?? '￿',
    },
    {
      id: 'quantity',
      label: 'Qty',
      width: 80,
      align: 'right',
      render: (item) => String(item.quantity),
      sortValue: (item) => item.quantity,
    },
    {
      id: 'each',
      label: 'Each',
      width: 100,
      align: 'right',
      render: (item) => (item.latest_value === null ? EMPTY : money(item.latest_value)),
      sortValue: (item) => Number(item.latest_value ?? 0),
    },
    {
      id: 'value',
      label: 'Value',
      width: 110,
      align: 'right',
      render: (item) => (
        <Typography
          variant="body2"
          sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
        >
          {item.holding_value === null ? EMPTY : money(item.holding_value)}
        </Typography>
      ),
      sortValue: (item) => Number(item.holding_value ?? 0),
    },
    {
      id: 'cost',
      label: 'Cost',
      width: 100,
      align: 'right',
      render: (item) => (item.holding_cost === null ? EMPTY : money(item.holding_cost)),
      sortValue: (item) => Number(item.holding_cost ?? 0),
    },
    {
      id: 'gain',
      label: 'Gain',
      width: 110,
      align: 'right',
      render: (item) => {
        const gain = toNumber(item.gain);
        if (gain === null) {
          return (
            <Typography variant="body2" color="text.disabled">
              {EMPTY}
            </Typography>
          );
        }
        return (
          <Typography
            variant="body2"
            sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
            color={gain >= 0 ? 'success.main' : 'error.main'}
          >
            {gain >= 0 ? '+' : ''}
            {money(item.gain)}
          </Typography>
        );
      },
      sortValue: (item) => Number(item.gain ?? 0),
    },
    {
      id: 'valued',
      label: 'Valued',
      width: 110,
      render: (item) => (
        <Typography variant="caption" color="text.secondary">
          {item.valued_at ? item.valued_at.slice(0, 10) : 'never'}
        </Typography>
      ),
      sortValue: (item) => item.valued_at ?? '',
    },
    {
      id: 'added',
      label: 'Added',
      width: 110,
      render: (item) => (
        <Typography variant="body2" color="text.secondary" noWrap>
          {item.added_at.slice(0, 10)}
        </Typography>
      ),
      sortValue: (item) => item.added_at,
    },
    {
      id: 'actions',
      label: '',
      width: editing ? 56 : 0,
      align: 'right',
      render: (item) =>
        editing ? (
          <ToolbarIconButton
            aria-label="Remove from collection"
            disabled={busy}
            onClick={(event) => {
              // The row opens the holding, so the delete has to stop the
              // click before it gets there.
              event.stopPropagation();
              onRemove(item);
            }}
          >
            <DeleteIcon fontSize="small" />
          </ToolbarIconButton>
        ) : null,
    },
  ];

  return (
    <DataTable
      rows={items}
      columns={columns}
      rowKey={(item) => item.id}
      onRowClick={onOpen}
      maxHeight={maxHeight}
      emptyMessage="Nothing here yet. Add cards from the Search page."
    />
  );
}
